/**
 * Keyboard shortcut catalog
 *
 * Single source of truth for every global keyboard shortcut the viewer
 * exposes. Each entry is plain data — keys, a stable id, a human-readable
 * description — and is referenced from a `useShortcut(SHORTCUTS.x, handler)`
 * call somewhere in the tree.
 *
 * To add a new shortcut:
 *   1. Add an entry here.
 *   2. Mount a `useShortcut(SHORTCUTS.yourId, () => ...)` call in whichever
 *      component owns the action.
 *
 * That's it — modifier resolution, capture-phase wiring, editable-target
 * gating, `preventDefault`, and conflict detection live in the manager
 * (see `shortcut-manager.ts`); the catalog stays pure data.
 *
 * Component-local interactions (Tab toggle inside a textarea, Escape inside
 * a modal, Enter to submit) intentionally do NOT live here. Those are
 * interaction-local UI behavior, not app commands, and forcing them
 * through a global registry would couple unrelated state.
 */
export const SHORTCUTS = {
    omnibarFocusAsk: {
        id: "omnibarFocusAsk",
        keys: ["Mod", "F"],
        description: "Focus omnibar (Ask mode)",
        allowInInputs: true,
    },
    omnibarFocusAdd: {
        id: "omnibarFocusAdd",
        keys: ["Mod", "D"],
        description: "Focus omnibar (Add mode)",
        allowInInputs: true,
    },
    // Alternate binding for the same Add-mode flow. Both ⌘D and ⌘E
    // drive the identical cycle handler (see `use-omnibar-focus-shortcut.ts`)
    // so users with either muscle memory land in the same place. Two
    // separate spec entries keeps the catalog data — and any future
    // cheatsheet UI — honest about both bindings being active. ⌘E would
    // otherwise be preempted by Chrome's address-bar-search default; the
    // capture-phase manager `preventDefault`s before the browser sees it.
    // No conflict with `openGroupSwitcher` (plain E): the conflict
    // detector compares Mod / Shift / Alt / key / allowInInputs, and
    // these specs differ in both Mod and allowInInputs.
    omnibarFocusAddAlt: {
        id: "omnibarFocusAddAlt",
        keys: ["Mod", "E"],
        description: "Focus omnibar (Add mode)",
        allowInInputs: true,
    },
    toggleWipBorder: {
        id: "toggleWipBorder",
        keys: ["Mod", "Shift", "B"],
        description: "Toggle WIP border",
        allowInInputs: true,
    },
    navHome: {
        id: "navHome",
        keys: ["Q"],
        description: "Open Home tab",
    },
    navCollections: {
        id: "navCollections",
        keys: ["W"],
        description: "Open Collections tab",
    },
    openGroupSwitcher: {
        id: "openGroupSwitcher",
        keys: ["E"],
        description: "Open group switcher",
    },
};
